import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FiCheck, FiExternalLink, FiPackage, FiTag, FiTrash2, FiX } from "react-icons/fi";
import useProductApproval from "../../../marketplace_admin/libs/useProductApproval";
import useProduct from "../../../marketplace_admin/libs/useProduct";
import useSetHotDeal from "../../../marketplace_admin/libs/useSetHotDeal";
import useSetTrendingDeal from "../../../marketplace_admin/libs/useSetTrendingDeal";
import useSetTodaysDeal from "../../../marketplace_admin/libs/useSetTodaysDeal";
import useUnmarkDeal from "../../../marketplace_admin/libs/useUnmarkDeal";
import useAdminDeleteProduct from "../../../marketplace_admin/libs/useAdminDeleteProduct";
import ProductPriceDisplay from "../../../Routes/Marketplace/components/ProductPriceDisplay";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import StatCard from "../../components/StatCard";
import ConfirmModal from "../../components/ConfirmModal";
import ProductApprovalChanges from "../components/ProductApprovalChanges";
import YesNoBadge from "../../components/YesNoBadge";
import useInfiniteScrollRef from "../../utils/useInfiniteScrollRef";
import Spinner from "../../../Spinner/Spinner";
import {
  filterAdminProducts,
  getAdminProductFilters,
  getAdminProductSizeEntries,
  getAdminProductStats,
  getAdminProductStock,
} from "../utils/adminProductDisplay";
import {
  getProductApprovalBadgeTone,
  getProductApprovalLabel,
  getProductApprovalStatus,
} from "../utils/productApprovalDisplay";

const PLACEHOLDER = "https://via.placeholder.com/48x48?text=—";

const AdminProductsPage = () => {
  const { data, error, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useProduct();
  const { setHotDeal, isLoading: hotLoading } = useSetHotDeal();
  const { setTrendingDeal, isLoading: trendingLoading } = useSetTrendingDeal();
  const { setTodaysDeal, isLoading: todayLoading } = useSetTodaysDeal();
  const { unmarkDeal, isLoading: unmarkLoading } = useUnmarkDeal();
  const { deleteProduct, isLoading: deleteLoading } = useAdminDeleteProduct();
  const { reviewProduct, isLoading: approvalLoading } = useProductApproval();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("pending");
  const [dealsProduct, setDealsProduct] = useState(null);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [approveProduct, setApproveProduct] = useState(null);
  const [reviewChangesProduct, setReviewChangesProduct] = useState(null);
  const [rejectProduct, setRejectProduct] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const lastRef = useInfiniteScrollRef(fetchNextPage, hasNextPage, isFetchingNextPage);
  const actionLoading = hotLoading || trendingLoading || todayLoading || unmarkLoading || deleteLoading || approvalLoading;

  const products = useMemo(
    () => data?.pages?.flatMap((page) => page.products || []) || [],
    [data]
  );

  const filtered = useMemo(
    () => filterAdminProducts(products, search, filter),
    [products, search, filter]
  );

  const stats = useMemo(() => getAdminProductStats(products), [products]);
  const deleteTarget = deleteProductId
    ? products.find((product) => product._id === deleteProductId)
    : null;

  const toggleDeal = async (product, type, active) => {
    try {
      if (active) {
        await unmarkDeal({ productId: product._id, type });
        toast.success("Deal removed");
      } else if (type === "hot") {
        await setHotDeal(product._id);
        toast.success("Marked as hot deal");
      } else if (type === "trending") {
        await setTrendingDeal(product._id);
        toast.success("Marked as trending");
      } else {
        await setTodaysDeal(product._id);
        toast.success("Marked as today's deal");
      }

      const field =
        type === "hot" ? "isHotDeal" : type === "trending" ? "isTrending" : "isTodaysDeal";
      setDealsProduct((prev) =>
        prev && prev._id === product._id ? { ...prev, [field]: !active } : prev
      );
    } catch {
      toast.error("Could not update deal");
    }
  };

  const handleDelete = async () => {
    if (!deleteProductId) return;
    try {
      await deleteProduct(deleteProductId);
      toast.success("Product deleted");
      setDeleteProductId(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete product");
    }
  };

  const handleApprove = async () => {
    if (!approveProduct) return;
    try {
      await reviewProduct({ productId: approveProduct._id, status: "approved" });
      toast.success("Product approved for marketplace");
      setApproveProduct(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not approve product");
    }
  };

  const handleReject = async () => {
    if (!rejectProduct) return;
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error("Please provide a rejection reason for the merchant");
      return;
    }
    try {
      await reviewProduct({ productId: rejectProduct._id, status: "rejected", reason });
      toast.success("Product rejected — merchant will see your feedback");
      setRejectProduct(null);
      setRejectReason("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not reject product");
    }
  };

  const columns = [
    {
      key: "product",
      label: "Product",
      render: (row) => (
        <div className="admin-product-cell">
          <img
            src={row.images?.[0] || PLACEHOLDER}
            alt=""
            className="admin-product-cell__thumb"
          />
          <div>
            <div className="admin-product-cell__name">{row.name}</div>
            <div className="admin-product-cell__meta">
              {row.brand ? `${row.brand} · ` : ""}
              {row.slug ? `#${row.slug.slice(0, 24)}` : row._id?.slice(-8)}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "category",
      label: "Category",
      render: (row) => row.category || "—",
    },
    {
      key: "price",
      label: "Price",
      render: (row) => <ProductPriceDisplay product={row} size="card" showSlash />,
    },
    {
      key: "stock",
      label: "Stock",
      render: (row) => {
        const total = getAdminProductStock(row);
        const tone = total === 0 ? "danger" : total <= 5 ? "warning" : "success";
        return (
          <span className={`dashboard-badge dashboard-badge--${tone === "success" ? "success" : tone}`}>
            {total === 0 ? "Out of stock" : `${total} units`}
          </span>
        );
      },
    },
    {
      key: "sizes",
      label: "Sizes",
      render: (row) => {
        const sizes = getAdminProductSizeEntries(row);
        if (!sizes.length) return <span style={{ color: "var(--dash-muted)" }}>—</span>;
        return (
          <div className="admin-size-chips">
            {sizes.map((entry) => (
              <span key={entry.label} className="admin-size-chip">
                {entry.label}: {entry.stock}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: "approval",
      label: "Review",
      render: (row) => {
        const status = getProductApprovalStatus(row);
        return (
          <div>
            <span className={`dashboard-badge dashboard-badge--${getProductApprovalBadgeTone(status)}`}>
              {getProductApprovalLabel(status)}
            </span>
            {status === "rejected" && row.rejectionReason && (
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--dash-muted)", maxWidth: 220 }}>
                {row.rejectionReason}
              </p>
            )}
            {status === "pending" && row.approvalChangeLog?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="dashboard-link"
                  style={{ fontSize: 12, padding: 0, border: "none", background: "none", cursor: "pointer" }}
                  onClick={() => setReviewChangesProduct(row)}
                >
                  {row.approvalChangeLog.length} change{row.approvalChangeLog.length === 1 ? "" : "s"} — review
                </button>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "deals",
      label: "Deals",
      render: (row) => (
        <div className="admin-deal-badges">
          {row.isHotDeal && <span className="dashboard-badge dashboard-badge--danger">Hot</span>}
          {row.isTodaysDeal && <span className="dashboard-badge dashboard-badge--warning">Today</span>}
          {row.isTrending && <span className="dashboard-badge dashboard-badge--info">Trending</span>}
          {!row.isHotDeal && !row.isTodaysDeal && !row.isTrending && (
            <span style={{ color: "var(--dash-muted)", fontSize: 13 }}>None</span>
          )}
        </div>
      ),
    },
    {
      key: "merchant",
      label: "Merchant",
      render: (row) =>
        row.creator ? (
          <Link to={`/admin/merchants/${row.creator}/products`} className="dashboard-link">
            View store
          </Link>
        ) : (
          "—"
        ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => {
        const status = getProductApprovalStatus(row);
        return (
        <div className="dashboard-table-actions">
          {status === "pending" && (
            <>
              <button type="button" className="dashboard-btn dashboard-btn--primary" disabled={actionLoading} onClick={() => setApproveProduct(row)}>
                <FiCheck aria-hidden="true" /> Approve
              </button>
              <button type="button" className="dashboard-btn dashboard-btn--danger" disabled={actionLoading} onClick={() => { setRejectProduct(row); setRejectReason(""); }}>
                <FiX aria-hidden="true" /> Reject
              </button>
            </>
          )}
          {status === "rejected" && (
            <button type="button" className="dashboard-btn dashboard-btn--primary" disabled={actionLoading} onClick={() => setApproveProduct(row)}>
              Approve
            </button>
          )}
          {row.creator && (
            <Link to={`/admin/merchants/${row.creator}/products/${row._id}`} className="dashboard-btn dashboard-btn--secondary">
              Details
            </Link>
          )}
          <button
            type="button"
            className="dashboard-btn dashboard-btn--secondary"
            onClick={() => setDealsProduct(row)}
          >
            <FiTag aria-hidden="true" /> Deals
          </button>
          <Link
            to={`/product/${row.slug || row._id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="dashboard-btn dashboard-btn--secondary"
          >
            <FiExternalLink aria-hidden="true" /> Storefront
          </Link>
          <button
            type="button"
            className="dashboard-btn dashboard-btn--danger"
            onClick={() => setDeleteProductId(row._id)}
          >
            <FiTrash2 aria-hidden="true" /> Delete
          </button>
        </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Products"
        subtitle="Review new listings before they go live on the marketplace"
        icon={FiPackage}
        actions={
          <Link to="/admin/deals" className="dashboard-btn dashboard-btn--primary">
            Deals hub
          </Link>
        }
      />

      {!isLoading && products.length > 0 && (
        <div className="dashboard-stat-grid">
          <StatCard label="Loaded products" value={stats.total} />
          <StatCard label="Pending review" value={stats.pending} />
          <StatCard label="Approved" value={stats.approved} />
          <StatCard label="Rejected" value={stats.rejected} />
          <StatCard label="Hot deals" value={stats.hot} />
          <StatCard label="Trending" value={stats.trending} />
          <StatCard label="Today's deals" value={stats.today} />
          <StatCard label="On sale" value={stats.onSale} />
          <StatCard label="Out of stock" value={stats.outOfStock} />
        </div>
      )}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, brand, or category…"
      >
        <div className="dashboard-filter-pills">
          {getAdminProductFilters().map((item) => (
            <button
              key={item.id}
              type="button"
              className={filter === item.id ? "active" : ""}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </FilterBar>

      {error && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Could not load products.</p>
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState
          icon={FiPackage}
          title="No products found"
          message={search || filter !== "all" ? "Try a different search or filter." : "No products listed yet."}
        />
      )}

      {filtered.length > 0 && (
        <>
          <DataTable
            columns={columns}
            rows={filtered.map((product) => ({
              ...product,
              _key: product._id,
            }))}
            loading={isLoading}
          />
          <div ref={lastRef} className="dashboard-load-more">
            {isFetchingNextPage && <Spinner />}
            {!hasNextPage && !isFetchingNextPage && (
              <span style={{ color: "var(--dash-muted)", fontSize: 13 }}>All products loaded</span>
            )}
          </div>
        </>
      )}

      {dealsProduct && (
        <div className="dashboard-modal-backdrop" onClick={() => setDealsProduct(null)} role="presentation">
          <div className="dashboard-modal admin-deals-modal" onClick={(e) => e.stopPropagation()} role="dialog">
            <h3>Manage deals</h3>
            <p style={{ marginTop: 0 }}>
              <strong>{dealsProduct.name}</strong>
              <br />
              <span style={{ color: "var(--dash-muted)", fontSize: 14 }}>{dealsProduct.category}</span>
            </p>
            <div className="admin-deals-modal__toggles">
              {[
                { type: "hot", label: "Hot deal", active: dealsProduct.isHotDeal },
                { type: "trending", label: "Trending", active: dealsProduct.isTrending },
                { type: "today", label: "Today's deal", active: dealsProduct.isTodaysDeal },
              ].map((deal) => (
                <div key={deal.type} className="admin-deals-modal__row">
                  <span>{deal.label}</span>
                  <YesNoBadge
                    value={deal.active}
                    trueText="Active"
                    falseText="Off"
                    falseVariant="neutral"
                  />
                  <button
                    type="button"
                    className={`dashboard-btn ${deal.active ? "dashboard-btn--secondary" : "dashboard-btn--primary"}`}
                    disabled={actionLoading}
                    onClick={() => toggleDeal(dealsProduct, deal.type, deal.active)}
                  >
                    {deal.active ? "Remove" : "Enable"}
                  </button>
                </div>
              ))}
            </div>
            <div className="dashboard-modal__actions">
              <button
                type="button"
                className="dashboard-btn dashboard-btn--secondary"
                onClick={() => setDealsProduct(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {reviewChangesProduct && (
        <div className="dashboard-modal-backdrop" onClick={() => setReviewChangesProduct(null)} role="presentation">
          <div
            className="dashboard-modal dashboard-modal--wide"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <h3>Merchant changes — {reviewChangesProduct.name}</h3>
            <p style={{ marginTop: 0, color: "var(--dash-muted)" }}>
              Review what changed before approving this listing.
            </p>
            <ProductApprovalChanges
              changes={reviewChangesProduct.approvalChangeLog}
              title="Changes awaiting review"
            />
            <div className="dashboard-modal__actions">
              <button
                type="button"
                className="dashboard-btn dashboard-btn--secondary"
                onClick={() => setReviewChangesProduct(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="dashboard-btn dashboard-btn--primary"
                onClick={() => {
                  setApproveProduct(reviewChangesProduct);
                  setReviewChangesProduct(null);
                }}
              >
                <FiCheck aria-hidden="true" /> Approve product
              </button>
            </div>
          </div>
        </div>
      )}

      {approveProduct && (
        <div className="dashboard-modal-backdrop" onClick={() => setApproveProduct(null)} role="presentation">
          <div
            className="dashboard-modal dashboard-modal--wide"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <h3>Approve product?</h3>
            <p style={{ marginTop: 0 }}>
              <strong>{approveProduct.name}</strong> will go live on the marketplace and become visible to
              customers.
            </p>
            {approveProduct.approvalChangeLog?.length > 0 ? (
              <ProductApprovalChanges
                changes={approveProduct.approvalChangeLog}
                title="Merchant changes awaiting review"
              />
            ) : (
              <p className="dashboard-form-hint">This is a new listing with no recorded edits.</p>
            )}
            <div className="dashboard-modal__actions">
              <button
                type="button"
                className="dashboard-btn dashboard-btn--secondary"
                onClick={() => setApproveProduct(null)}
                disabled={approvalLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dashboard-btn dashboard-btn--primary"
                onClick={handleApprove}
                disabled={approvalLoading}
              >
                {approvalLoading ? "Approving…" : "Approve for marketplace"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={Boolean(deleteProductId)}
        title="Delete product?"
        message={
          deleteTarget
            ? `Permanently delete "${deleteTarget.name}"? This cannot be undone.`
            : "Permanently delete this product?"
        }
        confirmLabel="Delete"
        danger
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteProductId(null)}
      />

      {rejectProduct && (
        <div className="dashboard-modal-backdrop" onClick={() => setRejectProduct(null)} role="presentation">
          <div className="dashboard-modal" onClick={(e) => e.stopPropagation()} role="dialog">
            <h3>Reject product</h3>
            <p style={{ marginTop: 0 }}>
              Tell <strong>{rejectProduct.name}</strong> why it cannot go live yet. The merchant will see this on their dashboard.
            </p>
            <label className="dashboard-form-field dashboard-form-field--full">
              <span>Rejection reason *</span>
              <textarea
                rows={5}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Images are blurry, description missing size details, price seems incorrect…"
                maxLength={500}
                required
              />
            </label>
            <div className="dashboard-modal__actions">
              <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => setRejectProduct(null)}>
                Cancel
              </button>
              <button type="button" className="dashboard-btn dashboard-btn--danger" disabled={approvalLoading} onClick={handleReject}>
                {approvalLoading ? "Rejecting…" : "Reject product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminProductsPage;
